/**
 * APP 环境跳转到新的页面
 */
import { Level, reportCustomMetric, reportError } from "../owl";
import { isHttpScheme } from "../utils";
import { BridgeType, callGrowthMSIBridge, callKNBBridge, callMSIBridge, getBridgeType } from "./base";

/* ----- MSI.openLink 类型声明 start ----- */
// https://msi-extend.sankuai.com/api?id=1302&tab=C
interface MSIOpenLinkProps {
  url: string;
  success?: (res: MSIOpenLinkSuccessCbParams) => void;
  fail?: (err: MSIOpenLinkFailCbParams) => void;
  complete?: () => void;
  routeMode?: "standard" | "popToSingleton" | "moveToTopSingleton";
  openWebByHttpUrl?: boolean;
}

interface MSIOpenLinkSuccessCbParams {
  errMsg: string; // 响应信息
  errno: number; // 响应码
}

interface MSIOpenLinkFailCbParams {
  errMsg: string; // 响应信息
  errno: number; // 响应码
}
/* ----- MSI.openLink 类型声明 end ----- */

/* ----- KNB.openPage 类型声明 start ----- */
// https://km.sankuai.com/collabpage/418094558
interface KNBOpenPageProps {
  url: string;
  // url 为 HTTP 协议时，内部会拼接 imeituan://www.meituan.com/web，并将 query 作为协议链接的参数
  // 容器跳转Scheme参数：https://km.sankuai.com/collabpage/19154204
  query?: {
    openInApp?: 1 | 2; // 1 - 美团APP打开，2 - 三方APP打开
    notitlebar?: 0 | 1; // 0 - 有默认标题栏，1 - 隐藏标题栏
    future?: 1 | 2 | 4; // notitilebar = 1 生效，1 - 动态标题栏，2 - 全屏沉浸式（仅安卓），4 - 隐藏虚拟按键（下巴）
  };
  success?: () => void;
  fail?: (err: any) => void;
}
/* ----- KNB.openPage 类型声明 end ----- */

interface OpenPageProps {
  url: string;
  onSuccess?: () => void;
  onFail?: (err: any) => void;
}

const bridgeType = getBridgeType();

type OpenPageErrMsg = 'success' | 'emptyUrl' | 'KNBBridgeFail' | 'MSIBridgeFail' | 'GrowthMSIBridgeFail';
// 上报 openPage 成功率
function reportOpenPageSuccessRate(isSuccess: boolean, errMsg: OpenPageErrMsg = "success") {
  reportCustomMetric({
    key: "openPage",
    value: isSuccess ? 1 : 0,
    tags: {
      errMsg,
      bridgeType,
    },
  });
}

export default function openPage(props: OpenPageProps) {
  if (!props.url) {
    reportError({
      name: "[openPage] openPageError",
      msg: `props.url is empty: ${JSON.stringify(props)}`,
      category: "bridgeError",
      level: Level.ERROR,
    });
    reportOpenPageSuccessRate(false, "emptyUrl");
    return;
  }
  switch (bridgeType) {
    case BridgeType.KNB:
      callKNBBridge("openPage", {
        url: props.url,
        // query 仅当 URL 为 HTTP 协议才生效
        query: {
          openInApp: 1, // 在美团 APP 中打开
        },
        // 不要依赖openPage success，如果当前success执行过长会导致下个webview卡主
        success: () => {
          if (typeof props.onSuccess === "function") {
            props.onSuccess();
          }
          reportOpenPageSuccessRate(true);
        },
        fail: function (error) {
          if (typeof props.onFail === "function") {
            props.onFail(error);
          }
          reportError({
            name: "[openPage] KNBOpenPageError",
            msg:  error,
            category: "bridgeError",
            level: Level.WARN,
          });
          reportOpenPageSuccessRate(false, "KNBBridgeFail");
        },
      } as KNBOpenPageProps);
      break;
    case BridgeType.MSI:
      callMSIBridge("openLink", {
        url: props.url,
        openWebByHttpUrl: isHttpScheme(props.url),
        success: () => {
          if (typeof props.onSuccess === "function") {
            props.onSuccess();
          }
          reportOpenPageSuccessRate(true);
        },
        fail: (err) => {
          if (typeof props.onFail === "function") {
            props.onFail(err);
          }
          reportError({
            name: "[openPage] MSIOpenLinkError",
            msg:  err,
            category: "bridgeError",
            level: Level.WARN,
          });
          reportOpenPageSuccessRate(false, "MSIBridgeFail");
        },
      } as MSIOpenLinkProps);
      break;
    case BridgeType.GROWTH_MSI:
      callGrowthMSIBridge("openLink", {
        url: props.url,
        openWebByHttpUrl: isHttpScheme(props.url),
        success: () => {
          if (typeof props.onSuccess === "function") {
            props.onSuccess();
          }
          reportOpenPageSuccessRate(true);
        },
        fail: (err) => {
          if (typeof props.onFail === "function") {
            props.onFail(err);
          }
          reportError({
            name: "[openPage] GrowthMSIOpenLinkError",
            msg:  err,
            category: "bridgeError",
            level: Level.WARN,
          });
          reportOpenPageSuccessRate(false, "GrowthMSIBridgeFail");
        },
      } as MSIOpenLinkProps);
      break;
    default:
      break;
  }
}
